/**
 * Contact Grouping Migration Runner — Admin Diagnostics card.
 *
 * Super-admin only. Two phases:
 *
 *   1. Dry-Run (GET /api/contact-grouping/dry-run) — writes nothing.
 *      Full report rendered on screen (summary + group plan +
 *      unassigned + contractor matches + near-duplicates).
 *
 *   2. Apply (POST /api/contact-grouping/apply) — gated by:
 *        a. A completed dry-run in this session (state token).
 *        b. Typed confirmation: user must type "APPLY" exactly.
 *
 *      Returns the prod batch_id. Surfaces a CustomEvent
 *      'contact-grouping-batch-applied' so the sibling Rollback card
 *      auto-refreshes and auto-expands to show the new batch.
 *
 * Tenant-scoped via the session JWT (endpoint reads company_id from
 * current_user). No company_id is sent client-side; the FE cannot
 * target another tenant by accident.
 *
 * data-testids:
 *   contact-grouping-runner-card
 *   contact-grouping-runner-toggle
 *   contact-grouping-runner-dryrun-btn
 *   contact-grouping-runner-summary
 *   contact-grouping-runner-apply-confirm-input
 *   contact-grouping-runner-apply-btn
 *   contact-grouping-runner-apply-result
 *   contact-grouping-runner-batch-id
 */

import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Play, ChevronDown, ChevronRight, Loader2, CheckCircle2, AlertTriangle,
  Users, Building2, FileQuestion, Copy, Layers,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ContactGroupingRunnerCard() {
  const [hidden, setHidden] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const [expanded, setExpanded] = useState(false);

  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunAt, setDryRunAt] = useState(null);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');

  // Auth check on mount — super-admin only.
  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((res) => {
        const user = res.data?.user || res.data || {};
        if (user.is_super_admin) {
          setHidden(false);
          setAdminEmail(user.email || '');
        }
      })
      .catch(() => {});
  }, []);

  const runDryRun = useCallback(async () => {
    setDryRunning(true);
    setDryRunResult(null);
    setApplyResult(null);  // any prior apply result is no longer relevant
    setConfirmInput('');
    try {
      const res = await axios.get(
        `${API}/api/contact-grouping/dry-run`, { headers: authHeaders() },
      );
      setDryRunResult(res.data);
      setDryRunAt(new Date());
      toast.success('Dry-run complete — review the plan below');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dry-run failed');
    } finally {
      setDryRunning(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    if (confirmInput.trim() !== 'APPLY') {
      toast.error('Type APPLY exactly to confirm');
      return;
    }
    if (!dryRunResult) {
      toast.error('Run a dry-run first');
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/api/contact-grouping/apply`, {}, { headers: authHeaders() },
      );
      setApplyResult(res.data);
      setConfirmInput('');
      toast.success(`Applied — batch ${res.data.batch_id.slice(0, 8)}…`);
      // Tell the sibling rollback card to refresh + auto-expand.
      window.dispatchEvent(new CustomEvent(
        'contact-grouping-batch-applied',
        { detail: { batch_id: res.data.batch_id } },
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [confirmInput, dryRunResult]);

  const copyBatchId = useCallback(async () => {
    if (!applyResult?.batch_id) return;
    try {
      await navigator.clipboard.writeText(applyResult.batch_id);
      toast.success('batch_id copied');
    } catch {
      toast.error('Copy failed — select the id manually');
    }
  }, [applyResult]);

  if (hidden) return null;

  const s = dryRunResult?.summary;
  const groupsToCreate = dryRunResult?.groups_to_create || [];
  const unassigned = dryRunResult?.unassigned || [];
  const nearDupes = dryRunResult?.near_duplicates || [];
  const contractorMatches = dryRunResult?.contractor_link_plan || [];

  // Detect collision-aware names (em-dash separator) in the plan so the
  // operator can see disambiguation at a glance.
  const dashedGroupNames = groupsToCreate
    .filter(g => (g.name || '').includes(' \u2014 ') && g.type === 'family')
    .map(g => g.name);

  return (
    <Card className="border-stone-300" data-testid="contact-grouping-runner-card">
      <CardHeader
        className="cursor-pointer flex flex-row items-start justify-between gap-4"
        onClick={() => setExpanded(!expanded)}
        data-testid="contact-grouping-runner-toggle"
      >
        <div className="flex items-start gap-3">
          {expanded ? <ChevronDown className="h-5 w-5 mt-0.5" /> : <ChevronRight className="h-5 w-5 mt-0.5" />}
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Contact Grouping Migration Runner
              <Badge className="bg-stone-100 text-stone-700 border-stone-300 border ml-1">
                super-admin · tenant-scoped
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              Two-phase migration runner for this tenant. Dry-Run plans the
              backfill (no writes). Apply commits under a fresh
              <code className="px-1 mx-0.5 text-[10px] bg-stone-100 rounded">batch_id</code>
              that is rollback-safe via the card below.
              {adminEmail && (
                <span className="block mt-1 text-[11px] text-stone-500">
                  Signed in as <strong>{adminEmail}</strong>
                </span>
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {/* Phase 1: Dry-Run */}
          <div className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Step 1 · Dry-Run</p>
                <p className="text-xs text-stone-600">
                  Read-only. No writes. Returns the full plan for review.
                </p>
              </div>
              <Button
                size="sm"
                onClick={runDryRun}
                disabled={dryRunning}
                data-testid="contact-grouping-runner-dryrun-btn"
              >
                {dryRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                Run Dry-Run
              </Button>
            </div>

            {s && (
              <div
                className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-2 border-t border-stone-200"
                data-testid="contact-grouping-runner-summary"
              >
                <Stat label="Total contacts" value={s.total_contacts} />
                <Stat label="Already grouped" value={s.contacts_already_grouped} />
                <Stat label="Groups to create" value={s.groups_to_create} highlight />
                <Stat label="Contacts to link" value={s.contacts_to_link} highlight />
                <Stat label="To mark Unassigned" value={s.contacts_to_mark_unassigned} />
                <Stat label="Needs-completion stamps" value={s.contacts_to_stamp_needs_completion} />
                <Stat label="Hygiene-suspect flags" value={s.contacts_to_flag_hygiene_suspect} />
                <Stat label="Contractors to link" value={s.contractors_to_link} />
                <Stat label="Contractors unlinked" value={s.contractors_unlinked} />
                <Stat label="Near-duplicate names" value={s.near_duplicate_group_names} warn />
                <Stat
                  label="Collision-disambiguated"
                  value={dashedGroupNames.length}
                  highlight
                />
                {dryRunAt && (
                  <Stat
                    label="Dry-run captured"
                    value={dryRunAt.toLocaleTimeString()}
                  />
                )}
              </div>
            )}

            {dryRunResult && (
              <div className="space-y-3 pt-2 border-t border-stone-200">
                {dashedGroupNames.length > 0 && (
                  <DetailBlock
                    title="Collision-aware (em-dash) family groups"
                    color="emerald"
                    items={dashedGroupNames}
                  />
                )}
                <DetailBlock
                  title={`Groups to create (${groupsToCreate.length})`}
                  color="sky"
                  items={groupsToCreate.map(g =>
                    `${g.type === 'company' ? '🏢' : '👪'}  ${g.name}  ·  ${g.members_planned} member${g.members_planned === 1 ? '' : 's'}`
                  )}
                />
                {nearDupes.length > 0 && (
                  <DetailBlock
                    title={`Near-duplicate name buckets (${nearDupes.length}) — review before apply`}
                    color="amber"
                    items={nearDupes.map(b => b.join('  ↔  '))}
                  />
                )}
                <DetailBlock
                  title={`Unassigned contacts (${unassigned.length}) — will receive needs_completion`}
                  color="stone"
                  items={unassigned.slice(0, 50).map(u =>
                    `${u.first_name || '(no first name)'}${u.hygiene_suspect ? ' ⚠ hygiene-suspect' : ''}`
                  )}
                  more={unassigned.length > 50 ? unassigned.length - 50 : 0}
                />
                <DetailBlock
                  title={`Contractor name-matches (${contractorMatches.length})`}
                  color="violet"
                  items={contractorMatches.map(m => `${m.contractor_company_name} → ${m.type}`)}
                />
              </div>
            )}
          </div>

          {/* Phase 2: Apply */}
          <div className={`rounded-md border p-3 space-y-3 ${
            dryRunResult ? 'border-rose-200 bg-rose-50/40' : 'border-stone-200 bg-stone-50/60 opacity-60'
          }`}>
            <div>
              <p className="text-sm font-semibold flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                Step 2 · Apply
              </p>
              <p className="text-xs text-stone-600">
                Writes the plan to the database under a fresh <code className="text-[10px] bg-stone-100 px-1 rounded">batch_id</code>.
                Reversible via the rollback card below.
              </p>
            </div>
            {!dryRunResult && (
              <p className="text-xs text-stone-500 italic">Run a dry-run first.</p>
            )}
            {dryRunResult && !applyResult && (
              <div className="space-y-2">
                <p className="text-xs text-rose-900">
                  Type <strong>APPLY</strong> to confirm. The batch is rollback-safe
                  — but apply still WRITES to {s?.contacts_to_link + s?.contacts_to_mark_unassigned} contact{(s?.contacts_to_link + s?.contacts_to_mark_unassigned) === 1 ? '' : 's'}.
                </p>
                <Input
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  placeholder="APPLY"
                  className="font-mono text-xs max-w-[200px]"
                  data-testid="contact-grouping-runner-apply-confirm-input"
                />
                <Button
                  size="sm"
                  onClick={runApply}
                  disabled={applying || confirmInput.trim() !== 'APPLY'}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="contact-grouping-runner-apply-btn"
                >
                  {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                  Apply migration
                </Button>
              </div>
            )}
            {applyResult && (
              <div
                className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-2"
                data-testid="contact-grouping-runner-apply-result"
              >
                <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" />
                  Applied successfully
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-600">batch_id:</span>
                  <code
                    className="text-xs bg-white border border-stone-200 rounded px-1.5 py-0.5"
                    data-testid="contact-grouping-runner-batch-id"
                  >
                    {applyResult.batch_id}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyBatchId} className="h-6 px-2">
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  <Stat label="Groups created" value={applyResult.groups_created} />
                  <Stat label="Contacts linked" value={applyResult.contacts_linked} />
                  <Stat label="Marked unassigned" value={applyResult.contacts_marked_unassigned} />
                  <Stat label="Contractors linked" value={applyResult.contractors_linked} />
                  <Stat label="Contractors unlinked" value={applyResult.contractors_unlinked} />
                  <Stat label="Snapshots written" value={applyResult.backup_snapshots_written} />
                </div>
                <p className="text-[11px] text-emerald-800 mt-1">
                  This batch is now visible in the Contact Grouping Rollback card below.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// -----------------------------------------------------------------------------

function Stat({ label, value, highlight, warn }) {
  return (
    <div className={`rounded-md p-2 ${
      warn ? 'bg-amber-50 border border-amber-200'
        : highlight ? 'bg-sky-50 border border-sky-200'
          : 'bg-white border border-stone-200'
    }`}>
      <p className="text-[10px] uppercase tracking-wide text-stone-500">{label}</p>
      <p className={`text-sm font-semibold ${
        warn ? 'text-amber-900' : highlight ? 'text-sky-900' : 'text-stone-900'
      }`}>
        {value ?? 0}
      </p>
    </div>
  );
}

function DetailBlock({ title, items, color = 'stone', more = 0 }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  const colorMap = {
    sky: 'border-sky-200 bg-sky-50/40',
    emerald: 'border-emerald-200 bg-emerald-50/40',
    amber: 'border-amber-200 bg-amber-50/40',
    violet: 'border-violet-200 bg-violet-50/40',
    stone: 'border-stone-200 bg-stone-50',
  };
  return (
    <div className={`rounded-md border ${colorMap[color]} text-xs`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-2.5 py-1.5 flex items-center justify-between gap-2 text-left"
      >
        <span className="font-medium">{title}</span>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {open && (
        <ul className="px-3 pb-2 space-y-0.5 max-h-72 overflow-y-auto">
          {items.map((it, i) => (
            <li key={i} className="text-[11px] text-stone-700 font-mono break-words">
              {it}
            </li>
          ))}
          {more > 0 && (
            <li className="text-[11px] italic text-stone-500">…and {more} more</li>
          )}
        </ul>
      )}
    </div>
  );
}
